import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { CommandPaletteService, PaletteCommand } from '@core/services/command-palette.service';
import { UserRolePermissionService } from '@core/services/user-role-permission.service';

import { IconComponent } from '../icon/icon.component';

interface PaletteGroup {
  group: string;
  commands: PaletteCommand[];
}

@Component({
  selector: 'app-command-palette',
  standalone: true,
  imports: [FormsModule, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './command-palette.component.html',
  styleUrl: './command-palette.component.scss'
})
export class CommandPaletteComponent {
  protected readonly palette = inject(CommandPaletteService);
  private readonly userRolePermission = inject(UserRolePermissionService);
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  protected readonly highlightIndex = signal(0);

  protected readonly filtered = computed<PaletteCommand[]>(() => {
    const q = this.palette.query().trim().toLowerCase();
    const all = this.palette
      .commands()
      .filter(c => !c.permission || this.userRolePermission.hasPermission(c.permission));
    if (!q) return [...all];
    return all.filter(c => {
      if (c.label.toLowerCase().includes(q)) return true;
      if (c.hint?.toLowerCase().includes(q)) return true;
      if (c.keywords?.some(k => k.toLowerCase().includes(q))) return true;
      return false;
    });
  });

  protected readonly groups = computed<PaletteGroup[]>(() => {
    const map = new Map<string, PaletteCommand[]>();
    for (const c of this.filtered()) {
      const list = map.get(c.group) ?? [];
      list.push(c);
      map.set(c.group, list);
    }
    return Array.from(map.entries()).map(([group, commands]) => ({ group, commands }));
  });

  /** Flat list in render order — used for arrow-key navigation. */
  private readonly flatCommands = computed<PaletteCommand[]>(() => this.groups().flatMap(g => g.commands));

  constructor() {
    // Reset highlight + focus the input whenever the palette opens or query changes.
    effect(() => {
      this.palette.open();
      this.palette.query();
      this.highlightIndex.set(0);
    });

    effect(() => {
      if (this.palette.open()) {
        queueMicrotask(() => this.searchInput()?.nativeElement.focus());
      }
    });
  }

  protected onQueryChange(value: string): void {
    this.palette.setQuery(value);
  }

  protected close(): void {
    this.palette.setOpen(false);
  }

  protected onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.close();
  }

  protected isHighlighted(cmd: PaletteCommand): boolean {
    const idx = this.flatCommands().indexOf(cmd);
    return idx === this.highlightIndex();
  }

  protected activate(cmd: PaletteCommand): void {
    this.close();
    cmd.run();
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const isOpen = this.palette.open();

    // Global toggle: Cmd+K (mac) or Ctrl+K (win/linux).
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.palette.toggle();
      return;
    }

    if (!isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    const list = this.flatCommands();
    if (list.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlightIndex.update(i => (i + 1) % list.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlightIndex.update(i => (i - 1 + list.length) % list.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const cmd = list[this.highlightIndex()];
      if (cmd) this.activate(cmd);
    }
  }
}
